/**
 * Super Admin — AI Credits
 *
 * The master tops up per-workspace AI credits and reads the ledger. Advisory
 * system: balances may be negative (charging never blocks the assistant), so
 * a red number here is a prompt to top up, not an outage.
 *
 * Not a security boundary — it renders for anyone who reaches /admin/ai/credits,
 * and every write is enforced by RLS (master + own uid + positive amount) and
 * every read by the RPC's is_platform_master() check. No client-side guard
 * belongs here.
 *
 * @module pages/admin/Credits
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Coins, Loader2, ShieldAlert } from "lucide-react";
import { YDMPageHero } from "../../components/brand";
import { useAuth } from "@/contexts/AuthContext";
import {
  clearCreditRate,
  fetchCreditBalances,
  fetchGlobalCreditRate,
  fetchRecentTransactions,
  isAiCreditsEnabled,
  setCreditRate,
  topUpCredits,
  type CreditLedgerRow,
  type WorkspaceCreditBalance,
} from "@/services/creditService";

const baht = (v: number): string => v.toLocaleString();

const REASON_LABEL: Record<string, string> = {
  topup: "เติมเครดิต",
  ai_usage: "เรียกใช้ AI",
  adjustment: "ปรับปรุงยอด",
};

const AdminCredits: React.FC = () => {
  const { session } = useAuth();
  const [rows, setRows] = useState<WorkspaceCreditBalance[]>([]);
  const [ledger, setLedger] = useState<CreditLedgerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Top-up form state, keyed to one workspace at a time.
  const [topupId, setTopupId] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  // Rates: one global default, plus an optional override per workspace.
  const [globalRate, setGlobalRate] = useState<number | null>(null);
  const [globalRateInput, setGlobalRateInput] = useState("");
  const [rateEditId, setRateEditId] = useState<string | null>(null);
  const [rateInput, setRateInput] = useState("");

  const workspaceName = useMemo(
    () => new Map(rows.map((r) => [r.workspaceId, r.workspaceName])),
    [rows],
  );

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [balances, transactions, global] = await Promise.all([
        fetchCreditBalances(),
        fetchRecentTransactions(30),
        fetchGlobalCreditRate(),
      ]);
      setRows(balances);
      setLedger(transactions);
      setGlobalRate(global);
      setGlobalRateInput(global === null ? "" : String(global));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load credits");
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleTopUp = async (workspaceId: string) => {
    const value = Number(amount);
    if (!(value > 0) || !session?.user.id) return;
    setBusy(true);
    try {
      await topUpCredits({ workspaceId, amount: value, note: note.trim(), userId: session.user.id });
      setTopupId(null);
      setAmount("");
      setNote("");
      setError(null);
      await load(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Top-up failed");
    }
    setBusy(false);
  };

  const handleSaveGlobalRate = async () => {
    const value = Number(globalRateInput);
    if (!(value >= 0)) return;
    setBusy(true);
    try {
      await setCreditRate(null, value);
      setError(null);
      await load(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to set the default rate");
    }
    setBusy(false);
  };

  const handleSaveRate = async (workspaceId: string) => {
    const value = Number(rateInput);
    if (!(value >= 0)) return;
    setBusy(true);
    try {
      await setCreditRate(workspaceId, value);
      setRateEditId(null);
      setRateInput("");
      setError(null);
      await load(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to set the rate");
    }
    setBusy(false);
  };

  const handleClearRate = async (workspaceId: string) => {
    setBusy(true);
    try {
      await clearCreditRate(workspaceId);
      setRateEditId(null);
      setError(null);
      await load(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear the override");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <YDMPageHero
        eyebrow="Super Admin"
        headline="AI Credits"
        subtitle="เติมเครดิตและตั้งอัตราการหักรายพื้นที่ทำงาน — หักเมื่อเรียก Gemini สำเร็จ 1 ครั้ง (แชท 1 ข้อความ ≈ 2-3 ครั้ง)"
        actions={
          !isAiCreditsEnabled() ? (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
              ระบบเครดิตปิดอยู่ (VITE_AI_CREDITS_ENABLED) — มิเตอร์ยังจดตามปกติ แต่ไม่หักเครดิต
            </span>
          ) : undefined
        }
      />

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-800">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-ink-900 animate-spin" aria-hidden="true" />
        </div>
      ) : (
        <>
          <section className="bg-white rounded-xl border border-neutral-200" data-global-rate>
            <div className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="text-sm font-semibold text-ink-900">อัตราการหักเครดิต (ค่ากลาง)</h2>
              <span className="text-xs text-neutral-700">
                ทุก workspace ใช้ค่านี้ เว้นตัวที่ตั้งค่าเฉพาะไว้ · ตั้ง 0 = ไม่หักเครดิต
              </span>
            </div>
            <div className="p-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-neutral-700">เรียก AI 1 ครั้ง =</span>
              <input
                type="number"
                min={0}
                step="0.5"
                value={globalRateInput}
                onChange={(e) => setGlobalRateInput(e.target.value)}
                className="w-28 rounded-md border border-neutral-300 px-3 py-1.5 text-sm tabular-nums"
                aria-label="อัตราการหักเครดิตค่ากลาง"
              />
              <span className="text-sm text-neutral-700">เครดิต</span>
              <button
                type="button"
                disabled={busy || globalRateInput === "" || Number(globalRateInput) < 0 || Number(globalRateInput) === globalRate}
                onClick={() => void handleSaveGlobalRate()}
                className="rounded-md bg-ink-950 px-4 py-1.5 text-sm font-semibold text-white hover:bg-ink-900 disabled:opacity-50"
              >
                บันทึกค่ากลาง
              </button>
              {globalRate !== null && (
                <span className="text-xs text-neutral-700">ตอนนี้ {globalRate.toLocaleString()} เครดิตต่อครั้ง</span>
              )}
            </div>
          </section>

          <section className="bg-white rounded-xl border border-neutral-200">
            <div className="px-4 py-3 border-b border-neutral-100">
              <h2 className="text-sm font-semibold text-ink-900">ยอดคงเหลือรายพื้นที่ทำงาน</h2>
            </div>
            <div className="divide-y divide-neutral-100">
              {rows.map((r) => (
                <div key={r.workspaceId} data-credit-row={r.workspaceId} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <Coins size={16} className="text-neutral-700 shrink-0" aria-hidden="true" />
                    <span className="font-medium text-ink-900 flex-1 min-w-0 truncate">{r.workspaceName}</span>
                    <span
                      className={`text-lg font-bold tabular-nums ${
                        r.balance < 0 ? "text-error-700" : r.balance === 0 ? "text-neutral-700" : "text-ink-900"
                      }`}
                    >
                      {baht(r.balance)}
                    </span>
                    <span className="text-xs text-neutral-700 tabular-nums w-40 text-right">
                      {r.lastTopupAt
                        ? `เติมล่าสุด ${new Date(r.lastTopupAt).toLocaleDateString("th-TH")}`
                        : "ยังไม่เคยเติม"}
                    </span>
                    {/* The effective rate, and whether it is this workspace's
                        own or inherited — an override that looks like the
                        default is the one nobody remembers setting. */}
                    <span
                      data-rate={r.workspaceId}
                      title={r.rateIsOverride ? "ตั้งเฉพาะ workspace นี้" : "รับค่ากลางมา"}
                      className={`text-xs px-2 py-0.5 rounded-full tabular-nums ${
                        r.rateIsOverride
                          ? "bg-primary-100 text-ink-900 font-semibold"
                          : "bg-neutral-100 text-neutral-700"
                      }`}
                    >
                      {r.rate.toLocaleString()}/ครั้ง{r.rateIsOverride ? "" : " (ค่ากลาง)"}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setRateEditId(rateEditId === r.workspaceId ? null : r.workspaceId);
                        setRateInput(String(r.rate));
                      }}
                      className="rounded-md border border-neutral-300 px-3 py-1 text-xs font-medium text-ink-900 hover:bg-neutral-50"
                    >
                      ตั้งเรท
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTopupId(topupId === r.workspaceId ? null : r.workspaceId);
                        setAmount("");
                        setNote("");
                      }}
                      className="rounded-md border border-neutral-300 px-3 py-1 text-xs font-medium text-ink-900 hover:bg-neutral-50"
                    >
                      เติมเครดิต
                    </button>
                  </div>
                  {rateEditId === r.workspaceId && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-neutral-700">เรียก AI 1 ครั้ง =</span>
                      <input
                        type="number"
                        min={0}
                        step="0.5"
                        value={rateInput}
                        onChange={(e) => setRateInput(e.target.value)}
                        className="w-24 rounded-md border border-neutral-300 px-3 py-1.5 text-sm tabular-nums"
                        aria-label={`อัตราการหักเครดิตของ ${r.workspaceName}`}
                      />
                      <span className="text-xs text-neutral-700">เครดิต</span>
                      {/* The field is prefilled with the EFFECTIVE rate, so on an
                          inheriting workspace one click would pin an override
                          identical to the default — an override nobody meant to
                          create and nobody would notice. Save only what differs. */}
                      <button
                        type="button"
                        disabled={
                          busy ||
                          rateInput === "" ||
                          Number(rateInput) < 0 ||
                          Number(rateInput) === r.rate
                        }
                        onClick={() => void handleSaveRate(r.workspaceId)}
                        className="rounded-md bg-ink-950 px-4 py-1.5 text-sm font-semibold text-white hover:bg-ink-900 disabled:opacity-50"
                      >
                        {r.rateIsOverride ? "บันทึกเรทเฉพาะ" : "ตั้งเรทเฉพาะ workspace นี้"}
                      </button>
                      {r.rateIsOverride && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void handleClearRate(r.workspaceId)}
                          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                        >
                          กลับไปใช้ค่ากลาง
                        </button>
                      )}
                    </div>
                  )}
                  {topupId === r.workspaceId && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="จำนวนเครดิต"
                        className="w-32 rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
                        aria-label={`จำนวนเครดิตสำหรับ ${r.workspaceName}`}
                      />
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="หมายเหตุ (เช่น รอบบิล ก.ย.)"
                        className="flex-1 min-w-[180px] rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
                        aria-label="หมายเหตุการเติม"
                      />
                      <button
                        type="button"
                        disabled={busy || !(Number(amount) > 0)}
                        onClick={() => void handleTopUp(r.workspaceId)}
                        className="rounded-md bg-ink-950 px-4 py-1.5 text-sm font-semibold text-white hover:bg-ink-900 disabled:opacity-50"
                      >
                        {busy ? "กำลังเติม…" : "ยืนยันเติม"}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-xl border border-neutral-200">
            <div className="px-4 py-3 border-b border-neutral-100 flex items-baseline justify-between gap-3">
              <h2 className="text-sm font-semibold text-ink-900">รายการล่าสุด</h2>
              <span className="text-xs text-neutral-700">30 รายการ · สมุดบัญชีแบบเพิ่มอย่างเดียว แก้ย้อนหลังไม่ได้</span>
            </div>
            {ledger.length === 0 ? (
              <p className="px-4 py-6 text-sm text-neutral-700">ยังไม่มีรายการ</p>
            ) : (
              <div className="divide-y divide-neutral-100">
                {ledger.map((l) => (
                  <div key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs">
                    <span className="text-neutral-700 tabular-nums w-32 shrink-0">
                      {new Date(l.createdAt).toLocaleString("th-TH", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                    <span className="font-medium text-ink-900 w-36 truncate">
                      {workspaceName.get(l.workspaceId) ?? l.workspaceId.slice(0, 8)}
                    </span>
                    <span className="text-neutral-700">{REASON_LABEL[l.reason] ?? l.reason}</span>
                    {l.note && <span className="text-neutral-700 flex-1 min-w-0 truncate">{l.note}</span>}
                    <span
                      className={`ml-auto font-semibold tabular-nums ${
                        l.amount < 0 ? "text-neutral-700" : "text-success-700"
                      }`}
                    >
                      {l.amount > 0 ? "+" : ""}
                      {baht(l.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default AdminCredits;
